import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { PlusIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const StudentConversationList = ({
  conversations,
  selectedConversation,
  onSelectConversation,
  currentUserId,
  loading,
  onNewChat
}) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex space-x-3">
              <div className="h-12 w-12 rounded-full bg-gray-700"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const getOtherParticipant = (conversation) => {
    const otherParticipantId = conversation.participants.find(id => id !== currentUserId);
    return {
      id: otherParticipantId,
      name: conversation.participant_names[otherParticipantId] || 'Unknown',
      type: conversation.participant_types[otherParticipantId],
      profilePic: conversation.participant_profile_pics?.[otherParticipantId]
    };
  };

  return (
    <div className="h-full flex flex-col w-full">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {/* Back button - visible only on mobile */}
            <button
              onClick={() => navigate('/student/dashboard')}
              className="md:hidden inline-flex items-center p-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-md transition-colors min-h-[44px] min-w-[44px] justify-center"
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-100">Messages</h2>
          </div>
          <button
            onClick={onNewChat}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors min-h-[44px] min-w-[44px] justify-center"
            title="Start new conversation"
            aria-label="Start new conversation"
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-4 text-center text-gray-400">
            <p>No conversations yet</p>
            <p className="text-sm mt-1">Start a new conversation with staff</p>
          </div>
        ) : (
          <div>
            {conversations.map((conversation) => {
              const otherParticipant = getOtherParticipant(conversation);
              const unreadCount = conversation.unread_count[currentUserId] || 0;
              const isSelected = selectedConversation?.id === conversation.id;

              return (
                <div
                  key={conversation.id}
                  onClick={() => onSelectConversation(conversation)}
                  className={`p-3 sm:p-4 border-b border-gray-800 cursor-pointer hover:bg-gray-800 transition-colors active:bg-gray-750 min-h-[72px] ${
                    isSelected ? 'bg-gray-800 hover:bg-gray-800' : ''
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {otherParticipant.profilePic ? (
                        <img
                          src={`data:image/jpeg;base64,${otherParticipant.profilePic}`}
                          alt={otherParticipant.name}
                          className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                          {otherParticipant.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-100 truncate">
                          {otherParticipant.name}
                        </p>
                        {conversation.last_message_at && (
                          <p className="text-xs text-gray-400">
                            {(() => {
                              const dateString = conversation.last_message_at.includes('+') || conversation.last_message_at.endsWith('Z')
                                ? conversation.last_message_at
                                : conversation.last_message_at + 'Z';
                              return formatDistanceToNow(new Date(dateString), { addSuffix: true });
                            })()}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-sm text-gray-400 truncate">
                          {conversation.last_message || 'No messages yet'}
                        </p>
                        {unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-600 text-white text-xs font-medium">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                        otherParticipant.type === 'student'
                          ? 'bg-green-900 text-green-300'
                          : 'bg-blue-900 text-blue-300'
                      }`}>
                        {otherParticipant.type === 'student' ? 'Student' : 'Staff'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentConversationList;
